import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scissors, Lock, Mail, User, Phone } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { resetSessionExpiredEmitter } from '../utils/sessionExpired';
import './Register.css';

const Register = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: ''
    });
    const [mounted, setMounted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        setMounted(true);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        if (formData.password !== formData.confirmPassword) {
            setError('Las contraseñas no coinciden');
            setLoading(false);
            return;
        }

        if (formData.password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            setLoading(false);
            return;
        }

        try {
            const requestData = {
                email: formData.email,
                password: formData.password,
                role: 'client',
                name: formData.name,
                phone: formData.phone || null
            };

            const response = await fetch(`${API_BASE_URL}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('token', data.access_token);
                localStorage.setItem('userRole', 'client');
                resetSessionExpiredEmitter();

                setSuccess('¡Cuenta creada! Redirigiendo a tus citas...');
                setTimeout(() => navigate('/client'), 1500);
            } else {
                setError(data.detail || 'Error en el registro');
            }
        } catch (err) {
            console.error('Registration error:', err);
            setError(`Error de conexión. Verifica que el backend esté ejecutándose.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-page">
            <div className="background-animation"></div>
            <div className={`register-card ${mounted ? 'fade-in-up' : ''}`}>
                <Link to="/" className={`register-logo ${mounted ? 'fade-in-down' : ''}`}>
                    <div className="logo-icon">
                        <Scissors size={28} strokeWidth={2.5} />
                    </div>
                    <span className="logo-text">GENUINO'S</span>
                </Link>
                <div className="card-header">
                    <h1 className="register-title">Crear cuenta de cliente</h1>
                    <p className="register-subtitle">Regístrate para ver y gestionar tus citas</p>
                </div>

                {(error || success) && (
                    <div className={`message ${error ? 'error-message' : 'success-message'}`}>
                        {error || success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="register-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Nombre completo</label>
                            <div className="input-wrapper">
                                <User className="input-icon" size={20} />
                                <input
                                    type="text"
                                    name="name"
                                    className="form-input"
                                    placeholder="Tu nombre completo"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Correo electrónico</label>
                            <div className="input-wrapper">
                                <Mail className="input-icon" size={20} />
                                <input
                                    type="email"
                                    name="email"
                                    className="form-input"
                                    placeholder="tu@email.com"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Contraseña</label>
                            <div className="input-wrapper">
                                <Lock className="input-icon" size={20} />
                                <input
                                    type="password"
                                    name="password"
                                    className="form-input"
                                    placeholder="Mínimo 6 caracteres"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Confirmar contraseña</label>
                            <div className="input-wrapper">
                                <Lock className="input-icon" size={20} />
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    className="form-input"
                                    placeholder="Repite tu contraseña"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Teléfono</label>
                            <div className="input-wrapper">
                                <Phone className="input-icon" size={20} />
                                <input
                                    type="tel"
                                    name="phone"
                                    className="form-input"
                                    placeholder="+57 300 123 4567"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <button type="submit" className="register-submit-btn" disabled={loading}>
                        <span>{loading ? 'Registrando...' : 'Crear cuenta'}</span>
                        <div className="btn-ripple"></div>
                    </button>
                </form>

                <div className="register-footer">
                    <p>¿Ya tienes cuenta? <Link to="/login" className="login-link">Inicia sesión</Link></p>
                </div>
            </div>
        </div>
    );
};

export default Register;
